import React, { useCallback, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Badge,
  Button,
  SectionCard,
  type BadgeColor,
} from '@enamelai/design-system'
import {
  AppointmentRequest,
  AppointmentResponse,
  AppointmentStatus,
  PatientWithInsurance,
  PaymentStatus,
} from '@dataTypes/appointment.type'
import { getFullName } from '@utils/fullname.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { createAppointment } from '@services/api/appointment.api'
import './AppointmentSummary.css'
import { noteOverlayAppModalBootstrapProps } from '@components/common/NoteEditorOverlay'
import { ScheduleAppointment } from '@components/layout/Modals'
import { refreshFromPatient } from '@store/reducers/patientmanagement.reducer'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { AppointmentStatusMap } from '@utils/appointment.util'
import { Icon as AddScheduleIcon } from '@enamelai/design-system/icons/Add_schedule'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Calendar'
import { Icon as HistoryIcon } from '@enamelai/design-system/icons/History'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import ProfileStatusBadge from '@pages/PatientProfile/shared/ProfileStatusBadge.component'
import PatientProfileDataTable, {
  type PatientProfileDataTableColumn,
} from '@pages/PatientProfile/shared/PatientProfileDataTable.component'

type PastAndUpcomingProps = {
  isPast: boolean
  appointmentsList: AppointmentResponse[]
  onUpdateData?: () => void
  isNoData?: boolean
  disableHeaderElements?: boolean
  pageSize?: number
}

const getStatusLabel = (appointment: AppointmentResponse): string =>
  AppointmentStatusMap[appointment.appt_status as AppointmentStatus] ?? ''

const getProviderName = (appointment: AppointmentResponse): string =>
  getFullName(appointment.provider, false, false)

const getAppointmentTypeName = (appointment: AppointmentResponse): string =>
  appointment.appointmenttype?.name ?? ''

const uniqueValues = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)))

const PAYMENT_STATUS_BADGE: Record<
  PaymentStatus,
  { label: string; color: BadgeColor }
> = {
  [PaymentStatus.PaymentCompleted]: { label: 'Paid', color: 'vivid-green' },
  [PaymentStatus.MissedPayments]: { label: 'Missed payment', color: 'red' },
}

const getPaymentStatusLabel = (appointment: AppointmentResponse): string =>
  PAYMENT_STATUS_BADGE[appointment.payment_status as PaymentStatus]?.label ??
  'N/A'

const renderPaymentStatus = (
  appointment: AppointmentResponse,
): React.ReactNode => {
  const badge = PAYMENT_STATUS_BADGE[
    appointment.payment_status as PaymentStatus
  ] ?? { label: 'N/A', color: 'gray' }
  return <Badge mode="tinted" color={badge.color} label={badge.label} />
}

const formatAppointmentDateTime = (appointment: AppointmentResponse): string =>
  `${moment(appointment.appt_date).format('MMM DD, YYYY')} (${moment(appointment.appt_start, 'HH:mm').format('hh:mm A')} - ${moment(appointment.appt_end, 'HH:mm').format('hh:mm A')})`

const FULL_COLUMNS: PatientProfileDataTableColumn<AppointmentResponse>[] = [
  {
    key: 'appt_date',
    label: 'Appointment date',
    width: 268,
    render: (appointment) => (
      <span className="patient-profile-appointments-date">
        {formatAppointmentDateTime(appointment)}
      </span>
    ),
    sortValue: (appointment) =>
      moment(
        `${moment(appointment.appt_date).format('YYYY-MM-DD')} ${appointment.appt_start}`,
        'YYYY-MM-DD HH:mm',
      ).valueOf(),
  },
  {
    key: 'appointment_type',
    label: 'Appointment type',
    render: getAppointmentTypeName,
    sortValue: getAppointmentTypeName,
  },
  {
    key: 'provider',
    label: 'Provider',
    render: getProviderName,
    sortValue: getProviderName,
  },
  {
    key: 'operatory',
    label: 'Assigned room',
    render: (appointment) => appointment.operatory?.name ?? '—',
    sortValue: (appointment) => appointment.operatory?.name ?? '',
  },
  {
    key: 'appt_status',
    label: 'Status',
    render: (appointment) => (
      <ProfileStatusBadge status={getStatusLabel(appointment)} />
    ),
    sortValue: getStatusLabel,
  },
  {
    key: 'payment_status',
    label: 'Payment status',
    render: renderPaymentStatus,
    sortValue: getPaymentStatusLabel,
  },
]

const MINIMAL_COLUMNS: PatientProfileDataTableColumn<AppointmentResponse>[] = [
  {
    key: 'appt_date',
    label: 'Appointment date',
    width: 260,
    ellipsis: true,
    render: (appointment) =>
      `${moment(appointment.appt_date, 'YYYY-MM-DD').format('MMM, D, YYYY')} (${moment(appointment.appt_start, 'HH:mm').format('hh:mm A')} - ${moment(appointment.appt_end, 'HH:mm').format('hh:mm A')})`,
  },
  {
    key: 'appointment_type',
    label: 'Appointment type',
    ellipsis: true,
    render: getAppointmentTypeName,
  },
  {
    key: 'appt_status',
    label: 'Status',
    width: 140,
    render: (appointment) => (
      <ProfileStatusBadge status={getStatusLabel(appointment)} />
    ),
  },
]

const searchTextFromAppointment = (appointment: AppointmentResponse): string =>
  [
    `${appointment.patient?.first_name ?? ''} ${appointment.patient?.last_name ?? ''}`,
    getAppointmentTypeName(appointment),
    `${appointment.provider?.first_name ?? ''} ${appointment.provider?.last_name ?? ''}`,
    appointment.operatory?.name ?? '',
  ].join(' ')

const FILTER_LABELS = {
  status: 'Status',
  provider: 'Provider',
  appointmentType: 'Appointment type',
}

const FILTER_VALUE_FROM_ROW = {
  status: getStatusLabel,
  provider: getProviderName,
  appointmentType: getAppointmentTypeName,
}

const PastAndUpcoming: React.FC<PastAndUpcomingProps> = ({
  isPast = false,
  appointmentsList,
  onUpdateData,
  isNoData = false,
  disableHeaderElements = false,
  pageSize,
}) => {
  const dispatch = useAppDispatch()
  const patient = useAppSelector((state) => state.patient.selectedPatient)
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] =
    useState<boolean>(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const filters = useMemo(
    () => ({
      status: uniqueValues(appointmentsList.map(getStatusLabel)),
      provider: uniqueValues(appointmentsList.map(getProviderName)),
      appointmentType: uniqueValues(
        appointmentsList.map(getAppointmentTypeName),
      ),
    }),
    [appointmentsList],
  )

  const handleOpenNewAppointment = useCallback((): void => {
    setIsSchedulingModalOpen(true)
  }, [])

  const handleSaveAppointment = async (
    apptDetails: Partial<AppointmentRequest>,
  ): Promise<void> => {
    showLoading({
      container: '.appointment-summary-page',
    })

    const response = await createAppointment(apptDetails)

    if (response.statusCode === 201) {
      setTimeout(() => {
        hideLoading('.appointment-summary-page')
        dispatch(refreshFromPatient())
        onUpdateData?.()
      }, 2000)
    } else {
      setTimeout(() => {
        hideLoading('.appointment-summary-page')
      }, 2000)
    }
  }

  const title = isPast ? 'Previous appointments' : 'Upcoming appointments'
  const showNewAppointment = !isPast && !disableHeaderElements
  const hasTable = !isNoData && appointmentsList.length > 0

  const newAppointmentButton = showNewAppointment ? (
    <Button variant="primary" size="md" onClick={handleOpenNewAppointment}>
      Set appointment
    </Button>
  ) : null

  const renderBody = (): React.ReactNode => {
    if (isNoData) {
      return (
        <ProfileEmptyState
          className={
            disableHeaderElements ? 'upcoming-appointments-no-data-message' : ''
          }
          icon={disableHeaderElements ? CalendarIcon : AddScheduleIcon}
          title={
            disableHeaderElements
              ? 'No upcoming appointments'
              : 'No appointments yet'
          }
          description={
            disableHeaderElements
              ? "You don't have any upcoming appointments on record."
              : "You don't have any appointments on record. Start by adding a new appointment."
          }
        />
      )
    }

    if (appointmentsList.length === 0) {
      return (
        <ProfileEmptyState
          icon={isPast ? HistoryIcon : CalendarIcon}
          title={isPast ? 'No past appointments' : 'No upcoming appointments'}
          description={
            isPast
              ? "You don't have any past appointments on record."
              : "You don't have any upcoming appointments on record."
          }
        />
      )
    }

    return (
      <PatientProfileDataTable
        className="patient-profile-appointments-table"
        columns={disableHeaderElements ? MINIMAL_COLUMNS : FULL_COLUMNS}
        rows={appointmentsList}
        rowKey={(appointment, index) =>
          appointment.appointment_id || `appt-${index}`
        }
        showPagination
        initialPageSize={pageSize ?? 10}
        enableSorting={!disableHeaderElements}
        tableTitle={disableHeaderElements ? undefined : title}
        headerEndSlot={newAppointmentButton ?? undefined}
        searchWithFilter={
          disableHeaderElements
            ? undefined
            : {
                filters,
                filterLabels: FILTER_LABELS,
                filterValueFromRow: FILTER_VALUE_FROM_ROW,
                searchTextFromRow: searchTextFromAppointment,
                placeholder: 'Search',
                className: 'patient-profile-appointments-search',
              }
        }
        emptyState={{
          title: 'No matching records',
          description: 'No appointments match the selected search or filters.',
        }}
      />
    )
  }

  return (
    <>
      {disableHeaderElements ? (
        renderBody()
      ) : (
        <SectionCard
          title={hasTable ? undefined : title}
          className="patient-profile-section patient-profile-appointments-card"
          headerRight={hasTable ? undefined : newAppointmentButton}
        >
          {renderBody()}
        </SectionCard>
      )}
      {isSchedulingModalOpen && (
        <ScheduleAppointment
          {...noteOverlayAppModalBootstrapProps}
          modalId="schedule-appointment-modal-stacked"
          isOpen={isSchedulingModalOpen}
          handleModal={() => setIsSchedulingModalOpen(false)}
          onSave={handleSaveAppointment}
          initialStartTime={moment().format('YYYY-MM-DD')}
          initialEndTime={moment().format('YYYY-MM-DD')}
          initialRoom={undefined}
          initialProvider={undefined}
          currentAppt={undefined}
          {...(patient && {
            preSelectedPatient: {
              patient_id: patient.patient_id,
              first_name: patient.first_name,
              last_name: patient.last_name,
              has_insurance: patient.has_insurance,
              pat_ins: patient.pat_ins,
            } as PatientWithInsurance,
          })}
        />
      )}
    </>
  )
}

export default PastAndUpcoming
